import { format } from '../../../services/HelperService';

/**
 * Browser runtime for VERSION-2 workflow cards (the workflowCard session profile).
 *
 * A v2 card definition stores its queries in DataShapeJson ({ version: 2, queries })
 * and its render script in LayoutJson ({ version: 2, render }). The render script is
 * ONE JavaScript expression that evaluates to the card's inner HTML (a string) for one
 * entity, against the same ctx contract the server tests it with
 * (WorkflowJavascriptContextScript / WorkflowCardSessionProfile, C#):
 *
 *   ctx.queries.<name>  this card's rows from each query (always an array)
 *   ctx.card            { entityID, entityType, stageKey, stageName, workflowName, title, description }
 *   ctx.fmt             the display helpers below — here bound to the user's real locale
 *                       formatters (HelperService 'format'); the server tests use locale-neutral shims
 *
 * The expression is compiled ONCE per distinct script text (module cache) and the
 * output is sanitized before it is injected (no script/style/iframe/form/event handlers).
 */

const safeString = (value) => (value ?? '').toString();

export const WORKFLOW_CARD_V2_VERSION = 2;

/** Parses a card's LayoutJson; returns { version, render } for v2 layouts, else null. */
export const parseWorkflowCardLayoutV2 = (layoutJson) => {
  try {
    const parsed = typeof layoutJson === 'string' ? JSON.parse(layoutJson || '{}') : layoutJson;
    if (!parsed || typeof parsed !== 'object') return null;
    const version = Number(parsed.version ?? parsed.Version ?? 1);
    if (version < WORKFLOW_CARD_V2_VERSION) return null;
    return { version, render: safeString(parsed.render ?? parsed.Render) };
  } catch {
    return null;
  }
};

/** Parses a card's DataShapeJson; returns { version, queries: [{ name, sql }] } for v2 shapes, else null. */
export const parseWorkflowCardDataShapeV2 = (dataShapeJson) => {
  try {
    const parsed = typeof dataShapeJson === 'string' ? JSON.parse(dataShapeJson || '{}') : dataShapeJson;
    if (!parsed || typeof parsed !== 'object') return null;
    const version = Number(parsed.version ?? parsed.Version ?? 1);
    if (version < WORKFLOW_CARD_V2_VERSION) return null;
    const queries = Array.isArray(parsed.queries ?? parsed.Queries) ? (parsed.queries ?? parsed.Queries) : [];
    return {
      version,
      queries: queries
        .map((q) => ({ name: safeString(q?.name ?? q?.Name).trim(), sql: safeString(q?.sql ?? q?.Sql ?? q?.query ?? q?.Query) }))
        .filter((q) => q.name),
    };
  } catch {
    return null;
  }
};

export const buildWorkflowCardDataShapeJson = (queries) => JSON.stringify({
  version: WORKFLOW_CARD_V2_VERSION,
  queries: (queries ?? [])
    .map((q) => ({ name: safeString(q?.name).trim(), sql: safeString(q?.sql ?? q?.query) }))
    .filter((q) => q.name && q.sql.trim()),
}, null, 2);

export const buildWorkflowCardLayoutJson = (render) => JSON.stringify({
  version: WORKFLOW_CARD_V2_VERSION,
  render: safeString(render),
}, null, 2);

/** HTML-escapes a value for safe interpolation into markup. */
export const escapeWorkflowCardHtml = (value) => (value === null || value === undefined
  ? ''
  : String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]));

// @oracle-source WorkflowCardFormatHelpers: ctx.fmt — the display helpers a card render script
// can call, exactly as the browser runs them: names, parameters (with defaults) and what each
// returns. Bound to the user's locale/currency formatters (HelperService 'format'); the server
// test harness mirrors the same names (WorkflowJavascriptContextScript.FormatHelperNames).
/**
 * ctx.fmt for the browser. Every value-taking helper is "blank safe": null, undefined or
 * '' in → '' out, and a formatter failure logs a warning and yields '' instead of
 * breaking the board.
 */
export const buildWorkflowCardFmt = () => {
  const blankSafe = (name, fn) => (value, ...rest) => {
    if (value === null || value === undefined || value === '') return '';
    try {
      const out = fn(value, ...rest);
      return out === null || out === undefined ? '' : String(out);
    } catch (error) {
      // A formatter failure must not break the board, but it must not hide either:
      // a silent '' reads as "no data" and sends the author chasing the query.
      console.warn(`workflow card ctx.fmt.${name} failed`, value, error);
      return '';
    }
  };
  return Object.freeze({
    /** esc(value) → HTML-escaped text (& < > " '). Wrap EVERY data value interpolated into markup. */
    esc: (value) => escapeWorkflowCardHtml(value),
    /** formatDate(value) → short date in the user's locale, e.g. "07/03/2026". value: Date | ISO string. */
    formatDate: blankSafe('formatDate', (v) => format.formatDate(v)),
    /** formatTime(value, amPM = true) → "3:05 pm"; amPM=false → 24-hour "15:05". */
    formatTime: blankSafe('formatTime', (v, amPM = true) => format.formatTime(v, amPM)),
    /** formatDateTime(value, amPM = true, shortDate = true) → "07/03/2026, 03:05:00 pm"; shortDate=false → "Sat, 7 Mar 2026, 03:05:00 pm". */
    formatDateTime: blankSafe('formatDateTime', (v, amPM = true, shortDate = true) => format.formatDateTime(v, amPM, shortDate)),
    /** formatDateMonthYear(value) → "Mar 2026". */
    formatDateMonthYear: blankSafe('formatDateMonthYear', (v) => format.formatDateMonthYear(v)),
    /** formatDateDayMonth(value) → "7 Mar". */
    formatDateDayMonth: blankSafe('formatDateDayMonth', (v) => format.formatDateDayMonth(v)),
    /** formatCurrency(value, emptyIfZero = false) → "$1,234.50" with the tenant's currency symbol; emptyIfZero → '' for 0. */
    formatCurrency: blankSafe('formatCurrency', (v, emptyIfZero) => format.formatCurrency(Number(v), emptyIfZero)),
    /** formatNumber(value, emptyIfZero = false) → "1,234.57" (two decimals, grouped). */
    formatNumber: blankSafe('formatNumber', (v, emptyIfZero) => format.formatNumber(Number(v), emptyIfZero)),
    /** formatInt(value, emptyIfZero = false) → "1,235" (rounded, grouped). */
    formatInt: blankSafe('formatInt', (v, emptyIfZero) => format.formatInt(Number(v), emptyIfZero)),
    /** formatPercentage(value, emptyIfZero = false) → "12.5%" from a FRACTION (0.125). */
    formatPercentage: blankSafe('formatPercentage', (v, emptyIfZero) => format.formatPercentage(Number(v), emptyIfZero)),
    /**
     * formatLength(value, unit, displayFormat) → a measured length (job line Width/Drop) in the
     * line's own unit and display format (mm, cm, inches with fractions, ...), exactly as the job
     * line grid shows it. ALWAYS pass the row's ProductBaseUnit and ProductBaseUnitFormat; never
     * format Width/Drop with formatNumber or append a unit yourself.
     */
    formatLength: blankSafe('formatLength', (v, unit, displayFormat) => format.formatLength(v, unit, displayFormat)),
    /** getCurrencySymbol() → the tenant's currency symbol, e.g. "$". */
    getCurrencySymbol: () => {
      try { return format.getCurrencySymbol(); } catch { return ''; }
    },
  });
};
// @oracle-source-end

let cachedFmt = null;
const getFmt = () => {
  cachedFmt ??= buildWorkflowCardFmt();
  return cachedFmt;
};

/**
 * The web app's Newtonsoft (Breeze) defaults add "$id"/"$type" members to every
 * serialized object — including the query map, the error map and each row. They are
 * transport metadata, never card data.
 */
export const isSerializerMetadataKey = (key) => typeof key === 'string' && key.startsWith('$');

export const stripSerializerMetadata = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj ?? {};
  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!isSerializerMetadataKey(key)) out[key] = value;
  }
  return out;
};

/** Normalises the server's per-query rows ({ name: rows[] }, PascalCase tolerant) into ctx.queries. */
export const buildWorkflowCardQueries = (queryRows, queryNames = []) => {
  const source = stripSerializerMetadata(queryRows);
  const queries = {};
  for (const [name, rows] of Object.entries(source)) {
    // Breeze may wrap arrays as { $values: [...] } when typed; accept both forms.
    const list = Array.isArray(rows) ? rows : (Array.isArray(rows?.$values) ? rows.$values : []);
    queries[name] = list.map((row) => (row && typeof row === 'object' ? stripSerializerMetadata(row) : row));
  }
  for (const name of queryNames) {
    if (name && !Array.isArray(queries[name])) queries[name] = [];
  }
  return queries;
};

/** Builds ctx.card from board/preview card facts. */
export const buildWorkflowCardContext = ({ entityID, entityType, stageKey, stageName, workflowName, title, description } = {}) => ({
  entityID: entityID ?? '',
  entityType: safeString(entityType),
  stageKey: safeString(stageKey),
  stageName: safeString(stageName),
  workflowName: safeString(workflowName),
  title: safeString(title),
  description: safeString(description),
});

const compiledRenderers = new Map();
const MAX_COMPILED = 50;

/**
 * Compiles a render expression once. Returns a function (ctx) => string, or throws a
 * SyntaxError (callers surface it as the card's error chip). Mirrors the server harness:
 * the script is the body of `return ( <script> );` with ctx as the only bound root.
 */
export const compileWorkflowCardRender = (script) => {
  const source = safeString(script).trim();
  if (!source) throw new Error('The card has no render script.');
  const cached = compiledRenderers.get(source);
  if (cached) return cached;

  // eslint-disable-next-line no-new-func
  const fn = new Function('ctx', `"use strict";\nreturn (\n${source}\n);`);
  if (compiledRenderers.size >= MAX_COMPILED) {
    compiledRenderers.delete(compiledRenderers.keys().next().value);
  }
  compiledRenderers.set(source, fn);
  return fn;
};

const FORBIDDEN_TAGS = new Set(['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form', 'link', 'meta', 'base']);

/**
 * Strips markup the card contract forbids (the server validator rejects it at design
 * time; this is the independent render-time layer): script-bearing elements, on*
 * handlers and javascript: URLs. Uses a detached template element so nothing executes.
 */
export const sanitizeWorkflowCardHtml = (html) => {
  const text = safeString(html);
  if (!text.trim()) return '';
  if (typeof document === 'undefined') return text;

  const template = document.createElement('template');
  template.innerHTML = text;
  const walk = (node) => {
    for (const child of Array.from(node.children ?? [])) {
      if (FORBIDDEN_TAGS.has(child.tagName.toLowerCase())) {
        child.remove();
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim().toLowerCase().replace(/\s+/g, '');
        // eslint-disable-next-line no-script-url
        if (name.startsWith('on') || value.startsWith('javascript:') || (name === 'srcdoc')) {
          child.removeAttribute(attr.name);
        }
      }
      walk(child);
    }
  };
  walk(template.content);
  return template.innerHTML;
};

/**
 * Renders a v2 card: { html, error }. Never throws — a failing script yields an empty
 * html and the error message, so the board shows its fallback plus an error chip.
 */
export const renderWorkflowCardV2 = ({ render, queries, card }) => {
  try {
    const fn = compileWorkflowCardRender(render);
    const ctx = {
      queries: queries ?? {},
      card: card ?? buildWorkflowCardContext(),
      fmt: getFmt(),
    };
    const value = fn(ctx);
    if (typeof value !== 'string') {
      return { html: '', error: 'The card render script did not return a string.' };
    }
    return { html: sanitizeWorkflowCardHtml(value), error: '' };
  } catch (error) {
    return { html: '', error: error?.message ?? String(error) };
  }
};
